/**
 * Builds "add to calendar" payloads (ICS text plus Google/Outlook URLs) for a
 * claimed volunteer shift. Times are entered in America/New_York, so local
 * wall-clock values are converted to real UTC instants here.
 */

const TZ = "America/New_York";

export type CalendarInvite = {
  title: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM or HH:MM:SS, null for an all-day entry */
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  description?: string | null;
  url?: string | null;
  uid?: string | null;
};

function offsetMs(ts: number) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ts));
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  const asUtc = Date.UTC(
    Number(map["year"]),
    Number(map["month"]) - 1,
    Number(map["day"]),
    Number(map["hour"]) % 24,
    Number(map["minute"]),
    Number(map["second"]),
  );
  return asUtc - ts;
}

/** Converts an America/New_York wall clock time to a UTC Date. */
function zonedToUtc(date: string, time: string) {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0);
  let ts = guess - offsetMs(guess);
  // Second pass settles the rare DST-boundary case.
  ts = guess - offsetMs(ts);
  return new Date(ts);
}

function stampUtc(value: Date) {
  return `${value.toISOString().replace(/[-:]/g, "").split(".")[0]}Z`;
}

function stampDate(date: string) {
  return date.replace(/-/g, "");
}

function addDays(date: string, days: number) {
  const [y, m, d] = date.split("-").map(Number);
  const next = new Date(Date.UTC(y, (m ?? 1) - 1, (d ?? 1) + days));
  return next.toISOString().slice(0, 10);
}

type Window = { allDay: true; start: string; end: string } | { allDay: false; start: Date; end: Date };

function windowFor(invite: CalendarInvite): Window {
  if (!invite.startTime) {
    return { allDay: true, start: stampDate(invite.date), end: stampDate(addDays(invite.date, 1)) };
  }
  const start = zonedToUtc(invite.date, invite.startTime);
  const end = invite.endTime
    ? zonedToUtc(invite.date, invite.endTime)
    : new Date(start.getTime() + 2 * 60 * 60 * 1000);
  return {
    allDay: false,
    start,
    end: end.getTime() > start.getTime() ? end : new Date(start.getTime() + 60 * 60 * 1000),
  };
}

function escapeText(value: string) {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

function fold(line: string) {
  if (line.length <= 73) return line;
  const chunks: string[] = [];
  let rest = line;
  while (rest.length > 73) {
    chunks.push(rest.slice(0, 73));
    rest = rest.slice(73);
  }
  chunks.push(rest);
  return chunks.join("\r\n ");
}

export function buildIcs(invite: CalendarInvite) {
  const win = windowFor(invite);
  const uid = invite.uid || `${stampDate(invite.date)}-${Math.abs(hash(invite.title))}@volunteer.t1dw.org`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//T1D Warriors//Warrior Volunteer//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stampUtc(new Date())}`,
    win.allDay
      ? `DTSTART;VALUE=DATE:${win.start}`
      : `DTSTART:${stampUtc(win.start)}`,
    win.allDay ? `DTEND;VALUE=DATE:${win.end}` : `DTEND:${stampUtc(win.end)}`,
    `SUMMARY:${escapeText(invite.title)}`,
  ];
  if (invite.location) lines.push(`LOCATION:${escapeText(invite.location)}`);
  if (invite.description) lines.push(`DESCRIPTION:${escapeText(invite.description)}`);
  if (invite.url) lines.push(`URL:${invite.url}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.map(fold).join("\r\n");
}

function hash(value: string) {
  let out = 0;
  for (let i = 0; i < value.length; i += 1) out = (out * 31 + value.charCodeAt(i)) | 0;
  return out;
}

export function googleCalendarUrl(invite: CalendarInvite) {
  const win = windowFor(invite);
  const dates = win.allDay
    ? `${win.start}/${win.end}`
    : `${stampUtc(win.start)}/${stampUtc(win.end)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: invite.title,
    dates,
  });
  if (invite.description) params.set("details", invite.description);
  if (invite.location) params.set("location", invite.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function outlookCalendarUrl(invite: CalendarInvite) {
  const win = windowFor(invite);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: invite.title,
  });
  if (win.allDay) {
    params.set("allday", "true");
    params.set("startdt", invite.date);
    params.set("enddt", invite.date);
  } else {
    params.set("startdt", win.start.toISOString());
    params.set("enddt", win.end.toISOString());
  }
  if (invite.description) params.set("body", invite.description);
  if (invite.location) params.set("location", invite.location);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function icsFileName(title: string) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
  return `${slug || "volunteer-shift"}.ics`;
}

/** Shared shape used by the manage page, the claim confirmation, and the email. */
export function inviteFromSignup(input: {
  eventTitle: string;
  slotTitle?: string | null;
  slotType?: string | null;
  quantity?: number | null;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  locationName?: string | null;
  address?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  manageUrl?: string | null;
  eventUrl?: string | null;
}): CalendarInvite {
  const title = input.slotTitle
    ? `${input.eventTitle} - ${input.slotTitle}`
    : input.eventTitle;
  const lines: string[] = [];
  if (input.slotType === "item" && input.quantity) {
    lines.push(`Bringing: ${input.quantity}`);
  } else if (input.slotTitle) {
    lines.push(`You signed up for: ${input.slotTitle}`);
  }
  if (input.contactName || input.contactEmail || input.contactPhone) {
    const contact = [input.contactName, input.contactEmail, input.contactPhone]
      .filter(Boolean)
      .join(" | ");
    lines.push(`Questions: ${contact}`);
  }
  if (input.manageUrl) lines.push(`Manage or release your spot: ${input.manageUrl}`);
  else if (input.eventUrl) lines.push(`Event details: ${input.eventUrl}`);

  return {
    title,
    date: input.date,
    startTime: input.startTime ?? null,
    endTime: input.endTime ?? null,
    location: [input.locationName, input.address].filter(Boolean).join(", ") || null,
    description: lines.join("\n") || null,
    url: input.manageUrl ?? input.eventUrl ?? null,
  };
}
